import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Lock, Ban, Save, RotateCcw } from "lucide-react";
import { api } from "@/lib/auth";
import RichTextField from "@/components/admin/RichTextField";
import TrustBoxSection, { TRUST_BOX_DEFAULTS } from "@/components/TrustBoxSection";
import { toast } from "sonner";

type TrustBoxContent = {
    enabled: boolean;
    icon_choice: "shield" | "lock" | "none";
    card_style: "card" | "editorial";
    trigger_text: string;
    card_headline: string;
    card_teaser: string;
    card_cta: string;
    headline: string;
    subheadline: string;
    body_html: string;
    claim: string;
    claim_subtitle: string;
};

const CARD_STYLE_OPTIONS: Array<{
    key: TrustBoxContent["card_style"];
    label: string;
    description: string;
}> = [
    {
        key: "card",
        label: "Karte mit Rahmen",
        description: "Kompakte Editorial-Karte mit dezentem Papier-Hintergrund und feiner Umrandung.",
    },
    {
        key: "editorial",
        label: "Editorial-Zwischenblock",
        description: "Ohne Card-Umrahmung — nur feine Linien oben/unten, zentriert, wirkt wie eine typografische Pause.",
    },
];

const ICON_OPTIONS: Array<{
    key: TrustBoxContent["icon_choice"];
    label: string;
    icon: React.ReactNode;
}> = [
    { key: "shield", label: "Schild", icon: <ShieldCheck size={22} strokeWidth={1.5} /> },
    { key: "lock", label: "Schloss", icon: <Lock size={22} strokeWidth={1.5} /> },
    { key: "none", label: "Kein Icon", icon: <Ban size={22} strokeWidth={1.5} /> },
];

export default function AdminTrustBoxPage() {
    const [content, setContent] = useState<TrustBoxContent>(TRUST_BOX_DEFAULTS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [showPreview, setShowPreview] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await api<{ items: Record<string, unknown> }>("/api/admin/site-content");
        if (res.ok && res.data) {
            const raw = (res.data.items?.["home.trust_box"] || {}) as Partial<TrustBoxContent>;
            setContent({ ...TRUST_BOX_DEFAULTS, ...raw });
        }
        setLoading(false);
        setDirty(false);
    }, []);

    useEffect(() => { void load(); }, [load]);

    const patch = <K extends keyof TrustBoxContent>(k: K, v: TrustBoxContent[K]) => {
        setContent((prev) => ({ ...prev, [k]: v }));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        const res = await api("/api/admin/site-content/home.trust_box", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ value: content }),
        });
        setSaving(false);
        if (res.ok) {
            setDirty(false);
            toast.success("Vertrauens-Box gespeichert.");
        } else {
            toast.error(res.error || "Speichern fehlgeschlagen.");
        }
    };

    const resetToDefaults = () => {
        if (!window.confirm("Standardtext wiederherstellen? Die aktuellen Inhalte werden überschrieben (Speichern erst danach).")) return;
        setContent({ ...TRUST_BOX_DEFAULTS });
        setDirty(true);
    };

    if (loading) return <div className="text-hf-muted text-sm">Wird geladen…</div>;

    return (
        <div data-testid="admin-trust-box">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <div className="hf-eyebrow">Startseite · Vertrauens-Box</div>
                    <h1 className="font-display text-3xl md:text-4xl mt-2">Vertrauens-Box</h1>
                    <p className="mt-2 text-hf-muted max-w-2xl">
                        Kompakte Editorial-Karte direkt unter &bdquo;Die Idee&ldquo;. Öffnet ein Modal
                        mit Diskretion-Statement, KI-Transparenz und Marken-Claim.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={resetToDefaults}
                        className="hf-btn hf-btn-ghost"
                        data-testid="admin-trust-box-reset"
                    >
                        <RotateCcw size={14} /> Standardtext
                    </button>
                    <button
                        type="button"
                        onClick={() => void save()}
                        disabled={!dirty || saving}
                        className="hf-btn hf-btn-primary"
                        data-testid="admin-trust-box-save"
                    >
                        <Save size={14} /> {saving ? "Speichere…" : dirty ? "Änderungen speichern" : "Gespeichert"}
                    </button>
                </div>
            </div>

            {/* Sichtbarkeits-Schalter */}
            <div
                className="mt-8 p-4 rounded-sm flex items-center justify-between gap-4"
                style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            >
                <div>
                    <div className="text-sm text-hf-ink font-semibold">Sektion auf der Startseite anzeigen</div>
                    <div className="text-xs text-hf-muted mt-1">
                        Ausgeschaltet: die Vertrauens-Box wird komplett versteckt (kein DOM-Rendering).
                    </div>
                </div>
                <label className="inline-flex items-center gap-3 cursor-pointer">
                    <span className="text-xs text-hf-muted">{content.enabled ? "Sichtbar" : "Versteckt"}</span>
                    <input
                        type="checkbox"
                        checked={content.enabled}
                        onChange={(e) => patch("enabled", e.target.checked)}
                        className="h-5 w-9 accent-[hsl(var(--hf-gold-dark))]"
                        data-testid="admin-trust-box-enabled"
                    />
                </label>
            </div>

            {/* Icon-Wahl */}
            <div
                className="mt-6 p-6 rounded-sm"
                style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            >
                <div className="hf-eyebrow" style={{ fontSize: 10 }}>Icon</div>
                <div className="mt-3 grid grid-cols-3 gap-3">
                    {ICON_OPTIONS.map((opt) => {
                        const active = content.icon_choice === opt.key;
                        return (
                            <button
                                key={opt.key}
                                type="button"
                                onClick={() => patch("icon_choice", opt.key)}
                                className="p-4 rounded-sm text-left transition-colors"
                                style={{
                                    background: active ? "hsl(var(--hf-gold-soft) / 0.6)" : "hsl(var(--hf-surface))",
                                    border: active ? "1px solid hsl(var(--hf-gold-dark))" : "1px solid hsl(var(--hf-line))",
                                }}
                                data-testid={`admin-trust-box-icon-${opt.key}`}
                            >
                                <div className="flex items-center gap-3">
                                    <span
                                        className="inline-flex items-center justify-center"
                                        style={{
                                            width: 40, height: 40, borderRadius: 8,
                                            background: active ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-surface-2))",
                                            color: active ? "hsl(var(--hf-bg))" : "hsl(var(--hf-ink))",
                                        }}
                                    >
                                        {opt.icon}
                                    </span>
                                    <span className="text-sm font-semibold text-hf-ink">{opt.label}</span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Karten-Style-Wahl */}
            <div
                className="mt-6 p-6 rounded-sm"
                style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            >
                <div className="hf-eyebrow" style={{ fontSize: 10 }}>Darstellungs-Variante</div>
                <p className="text-xs text-hf-muted mt-1">
                    Wie soll der Trigger auf der Startseite aussehen? Der Modal-Inhalt bleibt in beiden Varianten identisch.
                </p>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {CARD_STYLE_OPTIONS.map((opt) => {
                        const active = content.card_style === opt.key;
                        return (
                            <button
                                key={opt.key}
                                type="button"
                                onClick={() => patch("card_style", opt.key)}
                                className="p-4 rounded-sm text-left transition-colors"
                                style={{
                                    background: active ? "hsl(var(--hf-gold-soft) / 0.6)" : "hsl(var(--hf-surface))",
                                    border: active ? "1px solid hsl(var(--hf-gold-dark))" : "1px solid hsl(var(--hf-line))",
                                }}
                                data-testid={`admin-trust-box-style-${opt.key}`}
                                aria-pressed={active}
                            >
                                <div className="flex items-start gap-3">
                                    <span
                                        className="inline-flex items-center justify-center shrink-0"
                                        style={{
                                            width: 20, height: 20, borderRadius: 999,
                                            background: active ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-surface-2))",
                                            border: active ? "none" : "1px solid hsl(var(--hf-line))",
                                            marginTop: 2,
                                        }}
                                    >
                                        {active && (
                                            <span
                                                style={{
                                                    width: 8, height: 8, borderRadius: 999,
                                                    background: "hsl(var(--hf-bg))",
                                                }}
                                            />
                                        )}
                                    </span>
                                    <div>
                                        <div className="text-sm font-semibold text-hf-ink">{opt.label}</div>
                                        <div className="text-xs text-hf-muted mt-1 leading-relaxed">
                                            {opt.description}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Textfelder */}
            <div
                className="mt-6 p-6 rounded-sm space-y-5"
                style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            >
                <TextField
                    label={'Karten-Überschrift (kompakter Trigger unter „Die Idee")'}
                    value={content.card_headline}
                    onChange={(v) => patch("card_headline", v)}
                    testId="admin-trust-box-card-headline"
                />
                <TextField
                    label="Karten-Teaser (2–3 Zeilen)"
                    value={content.card_teaser}
                    onChange={(v) => patch("card_teaser", v)}
                    testId="admin-trust-box-card-teaser"
                    multiline
                />
                <TextField
                    label={'Karten-Button-Text (unterstrichener Link am Ende der Karte, z. B. „Unser Versprechen an Sie →")'}
                    value={content.card_cta}
                    onChange={(v) => patch("card_cta", v)}
                    testId="admin-trust-box-card-cta"
                />
                <TextField
                    label={'Trigger-Text (Legacy — wird aktuell nicht mehr angezeigt, dient nur als Fallback für die Karten-Überschrift falls dieses Feld leer ist)'}
                    value={content.trigger_text}
                    onChange={(v) => patch("trigger_text", v)}
                    testId="admin-trust-box-trigger-text"
                />
                <TextField
                    label="Überschrift (im Modal)"
                    value={content.headline}
                    onChange={(v) => patch("headline", v)}
                    testId="admin-trust-box-headline"
                />
                <TextField
                    label="Unterüberschrift (Gold-Zeile)"
                    value={content.subheadline}
                    onChange={(v) => patch("subheadline", v)}
                    testId="admin-trust-box-subheadline"
                />
                <RichTextField
                    label="Fließtext (mehrere Absätze)"
                    value={content.body_html}
                    onChange={(v) => patch("body_html", v)}
                    testId="admin-trust-box-body"
                    rows={10}
                    helpText="Mit Absätzen strukturieren. Fettdruck & Links erlaubt. Kein HTML-Zauber nötig."
                />
                <TextField
                    label="Marken-Claim (Gold-Verlauf, zentriert)"
                    value={content.claim}
                    onChange={(v) => patch("claim", v)}
                    testId="admin-trust-box-claim"
                    multiline
                />
                <TextField
                    label="Untertitel unter dem Claim"
                    value={content.claim_subtitle}
                    onChange={(v) => patch("claim_subtitle", v)}
                    testId="admin-trust-box-claim-subtitle"
                    multiline
                />
            </div>

            {/* Live-Vorschau */}
            <div className="mt-8">
                <button
                    type="button"
                    onClick={() => setShowPreview((v) => !v)}
                    className="hf-btn hf-btn-ghost text-xs"
                    data-testid="admin-trust-box-toggle-preview"
                >
                    {showPreview ? "Vorschau ausblenden" : "Vorschau einblenden"}
                </button>
                {showPreview && (
                    <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                            <div className="hf-eyebrow mb-2" style={{ fontSize: 10 }}>Trigger auf der Startseite</div>
                            <div
                                className="rounded-sm overflow-hidden"
                                style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
                            >
                                {/* Nur der Trigger, Modal geschlossen. */}
                                <TrustBoxSection override={content} />
                            </div>
                        </div>
                        <div>
                            <div className="hf-eyebrow mb-2" style={{ fontSize: 10 }}>Modal (geöffnet)</div>
                            <div
                                className="rounded-sm overflow-hidden"
                                style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
                            >
                                {/* Modal inline (nicht Portal), damit es im Preview-Block bleibt. */}
                                <TrustBoxSection override={content} forceOpen inlineModal />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/** Kleiner Text-Input-Wrapper — nur für diese Seite lokal, damit
 *  wir nicht auf einen externen TextField-Import angewiesen sind. */
function TextField({
    label, value, onChange, testId, multiline,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    testId?: string;
    multiline?: boolean;
}) {
    return (
        <label className="block">
            <span className="hf-eyebrow block mb-1" style={{ fontSize: 10 }}>{label}</span>
            {multiline ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 rounded-sm text-sm text-hf-ink outline-none"
                    style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                    data-testid={testId}
                />
            ) : (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 rounded-sm text-sm text-hf-ink outline-none"
                    style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                    data-testid={testId}
                />
            )}
        </label>
    );
}
